import React from 'react';
import * as Headless from '@headlessui/react';
import { SparklesIcon } from '@heroicons/react/16/solid';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';
import clsx from 'clsx';
import { Link } from 'react-router-dom';

import { Button } from '@oysterjs/ui/button';

import { CaraWorkspaceEffortLevel } from '~/api/graphql';
import { effortLevelDescription, effortLevelLabel, type EffortLevelChoice } from './effort-level';

/**
 * Panel motion matches the attachment picker (`cara-input-attachment-button.tsx`):
 * scale + fade on data-[closed]/data-[enter]/data-[leave]. Transform origin is
 * chosen from `anchor` so the panel grows from the corner nearest the trigger.
 */
const OPTIONS_PANEL_BASE_CLASS = clsx(
  'z-[100] w-80 rounded-xl bg-white p-1 shadow-lg ring-1 ring-black/5 focus:outline-none',
  'transition data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0',
  'data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in',
  'dark:bg-neutral-800 dark:ring-white/10'
);

const optionsPanelOriginClass = (anchor: 'top end' | 'bottom start') =>
  anchor === 'top end' ? 'origin-top-right' : 'origin-bottom-left';

const PILL_TRIGGER_CLASS = clsx(
  'inline-flex h-8 items-center gap-1 rounded-full border border-neutral-200 bg-white pl-3 pr-2',
  'text-xs font-medium text-neutral-700 transition-colors',
  'hover:border-neutral-300 hover:bg-neutral-100 data-[open]:bg-neutral-100',
  'focus:outline-none data-[focus]:ring-2 data-[focus]:ring-primary-500',
  'data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50',
  'dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-300',
  'dark:hover:border-white/20 dark:hover:bg-white/10 dark:data-[open]:bg-white/10'
);

// Auto is a setting rather than a level: it chooses one of the levels listed
// after it. The sparkle and the divider keep it from reading as a fourth level.
export const AutoEffortIcon = ({ className }: { className?: string }) => (
  <SparklesIcon
    aria-hidden="true"
    data-slot="icon"
    className={clsx('size-3.5 shrink-0 text-primary-600 dark:text-primary-400', className)}
  />
);

const EffortLevelOption = ({
  choice,
  upgradeNoteId
}: {
  choice: EffortLevelChoice;
  upgradeNoteId: string;
}) => {
  const id = React.useId();
  const titleId = `${id}-title`;
  const descriptionId = `${id}-description`;
  const reasonId = `${id}-reason`;
  // The link cannot sit inside the option, whose content is read as one name.
  const showReason = !!choice.unavailableReason && !choice.requiresUpgrade;
  return (
    <Headless.ListboxOption
      value={choice.level}
      disabled={!!choice.unavailableReason}
      aria-labelledby={titleId}
      aria-describedby={clsx(
        descriptionId,
        showReason && reasonId,
        choice.requiresUpgrade && upgradeNoteId
      )}
      className={clsx(
        'group flex cursor-default gap-2.5 rounded-lg px-2.5 py-2 text-left',
        'data-[focus]:bg-neutral-100 dark:data-[focus]:bg-white/10',
        'data-[disabled]:cursor-not-allowed'
      )}
    >
      <CheckIcon
        aria-hidden="true"
        className="mt-0.5 size-4 shrink-0 text-primary-600 dark:text-primary-400 invisible group-data-[selected]:visible"
      />
      <span className="min-w-0 flex-1">
        <span
          id={titleId}
          className="flex items-center gap-1 text-sm font-semibold text-neutral-950 dark:text-white group-data-[disabled]:text-neutral-400 dark:group-data-[disabled]:text-neutral-500"
        >
          {effortLevelLabel(choice.level)}
          {choice.level === CaraWorkspaceEffortLevel.Auto && <AutoEffortIcon />}
        </span>
        <span
          id={descriptionId}
          className="mt-0.5 block text-xs/5 text-neutral-500 dark:text-neutral-400 group-data-[disabled]:text-neutral-400 dark:group-data-[disabled]:text-neutral-500"
        >
          {effortLevelDescription(choice.level)}
        </span>
        {showReason && (
          <span
            id={reasonId}
            className="mt-1 block text-xs/5 font-medium text-amber-700 dark:text-amber-400"
          >
            {choice.unavailableReason}
          </span>
        )}
      </span>
    </Headless.ListboxOption>
  );
};

/**
 * Compact effort picker for the control row beside a workspace's start button.
 * Each level shows its name and what it does; a level the current selection
 * cannot run stays listed, disabled, with the reason, or with a link to upgrade
 * when the plan is the reason.
 *
 * `variant="pill"` matches the rounded controls of the compose box.
 */
export const EffortLevelSelect = ({
  value,
  onChange,
  choices,
  variant = 'button',
  anchor = 'top end',
  disabled = false,
  inField = false,
  className
}: {
  value: CaraWorkspaceEffortLevel;
  onChange: (level: CaraWorkspaceEffortLevel) => void;
  choices: readonly EffortLevelChoice[];
  variant?: 'button' | 'pill';
  /** Opens upward by default, since the control sits at the foot of a dialog. */
  anchor?: 'top end' | 'bottom start';
  disabled?: boolean;
  /**
   * The control sits in a fieldset Field, whose label already names it; an
   * aria-label as well would be read after the field label.
   */
  inField?: boolean;
  className?: string;
}) => {
  const selectedLabel = effortLevelLabel(value);
  const auto = value === CaraWorkspaceEffortLevel.Auto;
  const upgradeLevel = choices.find((choice) => choice.requiresUpgrade);
  const upgradeNoteId = React.useId();
  const name = 'Analysis depth';
  const buttonLabel = inField ? undefined : `${name}: ${selectedLabel}`;

  return (
    <Headless.Listbox value={value} onChange={onChange} disabled={disabled}>
      {variant === 'pill' ? (
        <Headless.ListboxButton
          aria-label={buttonLabel}
          className={clsx(PILL_TRIGGER_CLASS, auto && 'pl-2.5', className)}
        >
          {auto && <AutoEffortIcon />}
          {selectedLabel}
          <ChevronUpDownIcon
            aria-hidden="true"
            className="size-4 text-neutral-400 dark:text-neutral-500"
          />
        </Headless.ListboxButton>
      ) : (
        <Headless.ListboxButton
          as={Button}
          color="white"
          aria-label={buttonLabel}
          className={className}
        >
          {auto && <AutoEffortIcon />}
          {selectedLabel}
          <ChevronUpDownIcon data-slot="icon" aria-hidden="true" />
        </Headless.ListboxButton>
      )}
      <Headless.ListboxOptions
        transition
        anchor={{ to: anchor, gap: 8 }}
        className={clsx(OPTIONS_PANEL_BASE_CLASS, optionsPanelOriginClass(anchor))}
      >
        {choices.map((choice, i) => (
          <React.Fragment key={choice.level}>
            <EffortLevelOption choice={choice} upgradeNoteId={upgradeNoteId} />
            {choice.level === CaraWorkspaceEffortLevel.Auto && i < choices.length - 1 && (
              <div
                aria-hidden="true"
                className="mx-2.5 my-1 border-t border-neutral-950/5 dark:border-white/10"
              />
            )}
          </React.Fragment>
        ))}
        {upgradeLevel && (
          <div
            id={upgradeNoteId}
            className="mt-1 border-t border-neutral-950/5 px-2.5 pb-1.5 pt-2 text-xs/5 text-neutral-500 dark:border-white/10 dark:text-neutral-400"
          >
            <Link
              to="/app/settings/subscription"
              className="font-medium text-primary-600 hover:underline dark:text-primary-400"
            >
              Upgrade to a usage-based plan
            </Link>{' '}
            to use {effortLevelLabel(upgradeLevel.level)} analysis.
          </div>
        )}
      </Headless.ListboxOptions>
    </Headless.Listbox>
  );
};
